import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  IconButton,
  Text,
  useToast,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { supabase } from "../../lib/supabaseClient";
import EditUserModal from "../../components/admin/EditUserModal";

const PAGE_SIZE = 20;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const toast = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("user").select("*", { count: "exact" });

    if (searchQuery) {
      query = query.or(
        `name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,nick_name.ilike.%${searchQuery}%`
      );
    }

    const { data, error, count } = await query
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (error) {
      toast({
        title: "Error fetching users",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setUsers([]);
    } else {
      setUsers(data);
      setTotalPages(Math.ceil(count / PAGE_SIZE));
    }
    setLoading(false);
  }, [page, searchQuery, toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleApprove = async (userId) => {
    const { error } = await supabase
      .from("user")
      .update({ verified: true, approved_at: new Date().toISOString() })
      .eq("id", userId);

    if (error) {
      toast({
        title: "Approval failed",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } else {
      toast({
        title: "User approved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchUsers();
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleNewUser = () => {
    setSelectedUser(null);
    setIsEditModalOpen(true);
  };

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchTrigger();
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">
          회원
        </Heading>
        <Button colorScheme="blue" onClick={handleNewUser}>
          사용자 등록
        </Button>
      </Flex>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Flex mb={4}>
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="회원 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </InputGroup>
          <Button ml={2} onClick={handleSearchTrigger}>
            검색
          </Button>
        </Flex>

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner />
          </Flex>
        ) : (
          <>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>사용자 이름</Th>
                  <Th>닉네임</Th>
                  <Th>회사</Th>
                  <Th>이메일</Th>
                  <Th>연락처</Th>
                  <Th>등급</Th>
                  <Th>수익 비율</Th>
                  <Th>역할</Th>
                  <Th>가입신청</Th>
                  <Th>승인</Th>
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {users.map((user) => (
                  <Tr key={user.id}>
                    <Td>{user.name}</Td>
                    <Td>{user.nick_name}</Td>
                    <Td>{user.company}</Td>
                    <Td>{user.email}</Td>
                    <Td>{user.phone_number}</Td>
                    <Td>{user.class}</Td>
                    <Td>{user.profit_share ? `${user.profit_share}%` : "-"}</Td>
                    <Td>
                      {Array.isArray(user.role)
                        ? user.role.join(", ")
                        : user.role}
                    </Td>
                    <Td>{new Date(user.created_date).toLocaleDateString()}</Td>
                    <Td>
                      {user.verified ? (
                        <Badge colorScheme="green" variant="subtle">
                          승인됨
                        </Badge>
                      ) : (
                        <Button
                          size="xs"
                          colorScheme="blue"
                          onClick={() => handleApprove(user.id)}
                        >
                          승인
                        </Button>
                      )}
                    </Td>
                    <Td>
                      <Button size="xs" onClick={() => handleEdit(user)}>
                        수정
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <Flex justify="center" align="center" mt={6}>
              <IconButton
                icon={<ChevronLeftIcon />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1}
                size="sm"
              />
              <Text mx={4} fontSize="sm">
                {page} / {totalPages}
              </Text>
              <IconButton
                icon={<ChevronRightIcon />}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages}
                size="sm"
              />
            </Flex>
          </>
        )}
      </Box>

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={selectedUser}
        onSave={fetchUsers}
      />
    </Box>
  );
};

export default AdminUsers;
